import { Box, Skeleton, useMediaQuery } from '@mui/material';
import { TABLET_COLUMNS, TABLET_QUERY } from '@components/Common/Table/constants';
import './index.scss';

/**
 * How much of a cell each placeholder bar fills, cycled down the rows.
 *
 * These are fills *inside* a cell, not the cells themselves. They used to be
 * the cell widths, laid out with flex and a gap, which meant the second bar of
 * every row started at a different x — so twenty rows read as a ragged
 * cascade rather than as the fixed-layout table that was about to replace it.
 * The grid owns the columns now; this only varies the ink in them.
 */
const COL_FILLS = [
  ['86%', '54%', '62%', '44%'],
  ['72%', '66%', '48%', '58%'],
  ['92%', '46%', '70%', '38%'],
  ['78%', '72%', '52%', '50%'],
  ['66%', '58%', '76%', '42%'],
];

/** Header labels are short and even; they do not need the body's stagger. */
const HEAD_FILLS = ['58%', '46%', '52%', '40%'];

export type SkeletonVariant = 'rows' | 'workspace';

interface SkeletonLoaderProps {
  /** Number of placeholder rows. Ignored by `workspace`. */
  rows?: number;
  /**
   * Column count, to match the `selectors` of the MintlyTable this stands in
   * for. Capped at three on tablet, exactly as the real table caps itself.
   */
  columns?: number;
  variant?: SkeletonVariant;
  /**
   * The rows carry a status chip, so reserve a chip's height rather than a line of text.
   *
   * A chip is 22px against a 18.75px line box, which made the jobs list's skeleton 3.25px short
   * per row and its rows jump on load. Opt-in: a table of plain words would be too tall with it.
   */
  statusChip?: boolean;
}

/**
 * The loading placeholder, in the two shapes this console actually loads.
 *
 * It used to be table-shaped only, which is why record pages kept a spinner instead: a row skeleton
 * over a two-column workspace is a worse loading state than the spinner it would replace, so
 * "always use SkeletonLoader" was advice pages were right to ignore. The variants remove the
 * reason to ignore it.
 *
 * It also declared a `height` prop it never read. Removed — a prop that does nothing is worse than
 * a missing one, because call sites pass it and believe it works. A third `cards` variant went the
 * same way: it was built, documented, and never once rendered.
 *
 * **A skeleton is a silhouette, not a decoration.** Every number here is the number the real
 * surface uses, read from the same token or the same exported constant, because the one thing this
 * component must do is not move the page when content arrives. It stopped doing that when the
 * responsive tiers landed on MintlyTable and EntityWorkspace and this file was not in the commit:
 * it kept its own 600/900px breakpoints and its own row metrics, and the two shapes drifted apart
 * on every tier. Do not reintroduce a literal here — reach for the token the real surface reads.
 */
export default function SkeletonLoader({
  rows = 8,
  columns = 4,
  variant = 'rows',
  statusChip = false,
}: SkeletonLoaderProps) {
  const isTablet = useMediaQuery(TABLET_QUERY);

  if (variant === 'workspace') {
    /* The EntityWorkspace shell: record bar, identity rail, tab bar, section cards, activity rail.
       Drawing only the rail and the cards left the two strips out, so content landed some 80px
       below where the silhouette had put it. */
    return (
      <Box className="skeleton-ws">
        <Box className="skeleton-ws__recordbar">
          <Skeleton className="skeleton-ws__bar skeleton-ws__bar--back" />
          <Skeleton className="skeleton-ws__bar skeleton-ws__bar--crumb" />
        </Box>

        <Box className="skeleton-ws__cols">
          <Box className="skeleton-ws__rail">
            <Skeleton variant="circular" className="skeleton-ws__avatar" />
            <Skeleton className="skeleton-ws__bar skeleton-ws__bar--name" />
            <Skeleton className="skeleton-ws__bar skeleton-ws__bar--sub" />
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="skeleton-ws__bar skeleton-ws__bar--glance" />
            ))}
          </Box>

          <Box className="skeleton-ws__center">
            <Box className="skeleton-ws__tabs">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="skeleton-ws__bar skeleton-ws__bar--tab" />
              ))}
            </Box>
            <Box className="skeleton-ws__scroll">
              {[...Array(3)].map((_, i) => (
                <Box key={i} className="skeleton-ws__card">
                  <Box className="skeleton-ws__cardhead">
                    <Skeleton className="skeleton-ws__bar skeleton-ws__bar--cardtitle" />
                  </Box>
                  <Box className="skeleton-ws__cardbody">
                    <Skeleton className="skeleton-ws__bar skeleton-ws__bar--field" />
                    <Skeleton className="skeleton-ws__bar skeleton-ws__bar--field" />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className="skeleton-ws__rail-activity">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="skeleton-ws__bar skeleton-ws__bar--entry" />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  /* The table caps itself at three columns on tablet and moves the rest into a row detail. Match
     it here rather than in CSS: `repeat()` cannot take a clamped count, and hiding the overflow
     cells would leave the grid sized for columns that are not on screen. */
  const capped = isTablet && columns > TABLET_COLUMNS;
  const cols = Math.max(1, capped ? TABLET_COLUMNS : columns);

  return (
    <Box
      className={`skeleton-table${capped ? ' skeleton-table--capped' : ''}${
        statusChip ? ' skeleton-table--chips' : ''
      }`}
      /* The one value only the render knows. The rule that consumes it stays in the stylesheet. */
      style={{ '--skeleton-cols': cols } as React.CSSProperties}
    >
      <Box className="skeleton-table__head">
        {[...Array(cols)].map((_, i) => (
          <Box key={i} className="skeleton-table__cell">
            <Skeleton
              className="skeleton-table__bar skeleton-table__bar--head"
              style={{ width: HEAD_FILLS[i % HEAD_FILLS.length] }}
            />
          </Box>
        ))}
        {capped && <Box className="skeleton-table__cell skeleton-table__cell--toggle" />}
      </Box>

      {[...Array(rows)].map((_, rowIdx) => {
        const fills = COL_FILLS[rowIdx % COL_FILLS.length];
        return (
          <Box key={rowIdx} className="skeleton-table__row">
            {[...Array(cols)].map((_, colIdx) => (
              <Box key={colIdx} className="skeleton-table__cell">
                <Skeleton
                  className="skeleton-table__bar"
                  style={{ width: fills[colIdx % fills.length] }}
                />
              </Box>
            ))}
            {capped && (
              <Box className="skeleton-table__cell skeleton-table__cell--toggle">
                <Skeleton className="skeleton-table__bar skeleton-table__bar--toggle" />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
